import { ReactNode, Suspense } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { Aggregation } from '@oaknorth/aggregation';
import { Calc } from '@oaknorth/calc';
import { Climate, ClimateModelValidator } from '@oaknorth/climate';
import { ClimateScorecard, ClimateScorecardVerifyEmail } from '@oaknorth/climate-scorecard';
import { highchartsi18next } from '@oaknorth/component-charts';
import {
  Box,
  ErrorBoundary,
  Flex,
  LegacyMode,
  Main,
  ModernMode,
  ONCILogoRingAndTextColour,
  PADDING_X_MODERN,
  ThemeProvider,
  Toaster,
} from '@oaknorth/component-core-v2';
import { yupI18next } from '@oaknorth/component-forms';
import { ExcludedBorrowerOverview, ExcludedBorrowersPage } from '@oaknorth/domain-explainability';
import { Explainability } from '@oaknorth/explainability';
import { FileRepository } from '@oaknorth/file-repository';
import { Ingestion } from '@oaknorth/ingestion';
import { LoanbookAnalysis } from '@oaknorth/loanbook-analysis';
import { Monitoring } from '@oaknorth/monitoring';
import {
  CREForecastAssumptions,
  CustomStressScenarios,
  ForecastAssumptions,
  Scenarios,
  ScenarioValidation,
} from '@oaknorth/scenarios';
import { FeatureFlagsProvider, useFlag } from '@oaknorth/util-feature-flags';
import { I18nProvider, initI18n } from '@oaknorth/util-intl';
import { MediaContextProvider } from '@oaknorth/util-responsive';
import { RestProvider } from '@oaknorth/util-rest';
import {
  createBrowserRouter,
  Helmet,
  HelmetProvider,
  Link,
  Outlet,
  PageNotFound,
  RouterProvider,
  useLocation,
} from '@oaknorth/util-routing';
import { useInitialiseRUM } from '@oaknorth/util-rum/initialiser';
import { useInitAnalytics, useTrackPageViews } from '@oaknorth/util-tracking';
import { UserContextProvider, useUserInfo } from '@oaknorth/util-user';

import { NewHomeAlertDialog } from './HomePage/NewHomeAlertDialog';
import { FeatureFlag } from './constants';
import { Diagnostics } from './diagnostics';
import { AppReleaseNotes } from './release-notes';
import { RoutingErrorBoundary } from './RoutingErrorBoundary';
import { Support } from './support';
import { SuspenseLoading } from './SuspenseLoading';
import { LandingRedirect } from './utils';

const i18n = initI18n({}, highchartsi18next, yupI18next);

/**
 * If the user is not an ONCI user, this renders page not found
 * Otherwise renders any matching child routes
 */
function NotFoundIfNotONUser() {
  const user = useUserInfo();

  if (!user?.isONUser) {
    return <PageNotFound />;
  }
  return <Outlet />;
}

function LoanbookAnalysisSwitcher({ isLoanbookAnalysisFeature }: { isLoanbookAnalysisFeature: boolean }) {
  const hasLoanbookAnalysis = useFlag<boolean>(FeatureFlag.HAS_LOANBOOK_ANALYSIS);

  if (hasLoanbookAnalysis && isLoanbookAnalysisFeature) {
    return <Outlet />;
  }

  if (!hasLoanbookAnalysis && !isLoanbookAnalysisFeature) {
    return <Outlet />;
  }
  return <PageNotFound />;
}

/**
 * Component to contain various initialisation hooks which need to be inside the relevant context providers
 */
function AppInit({ children }: { children: JSX.Element }) {
  useInitialiseRUM();
  useInitAnalytics();
  useTrackPageViews();
  return children;
}

const PADDING_X_NARROW = '16%';
function LegacyLayout({ children }: { children: ReactNode }) {
  const hasLoanbookAnalysis = useFlag<boolean>(FeatureFlag.HAS_LOANBOOK_ANALYSIS);
  const location = useLocation();
  // The scorecard product has a bespoke layout as it is a publicly accessible feature
  const inScorecard = location.pathname.includes('/climate-scorecard');

  return (
    <LegacyMode>
      <Flex flexDirection="column" maxWidth="100vw">
        {!inScorecard && !hasLoanbookAnalysis ? (
          <Box as="nav" paddingX={PADDING_X_NARROW} paddingTop={6}>
            <Link to="/">
              <Box width={32} color="on-logo-grey">
                <ONCILogoRingAndTextColour />
              </Box>
            </Link>
          </Box>
        ) : null}
        <Main>{children}</Main>
        {!hasLoanbookAnalysis ? <NewHomeAlertDialog /> : null}
        <Toaster />
      </Flex>
    </LegacyMode>
  );
}

function LoanbookAnalysisLayout({ children }: { children: ReactNode }) {
  return (
    <ModernMode>
      <Flex
        flexDirection="column"
        maxWidth="100vw"
        padding={PADDING_X_MODERN}
        // eslint-disable-next-line i18next/no-literal-string -- css props not user facing text
        bgImage="linear-gradient(180deg, {colors.gray.200}, {colors.gray.50}, {colors.gray.50}, {colors.gray.50})"
      >
        <Main>
          <Box background="none">{children}</Box>
        </Main>
        <Toaster />
      </Flex>
    </ModernMode>
  );
}

function Layout({ children }: { children: ReactNode }) {
  const hasLoanbookAnalysis = useFlag<boolean>(FeatureFlag.HAS_LOANBOOK_ANALYSIS);
  return hasLoanbookAnalysis ? (
    <LoanbookAnalysisLayout>{children}</LoanbookAnalysisLayout>
  ) : (
    <LegacyLayout>{children}</LegacyLayout>
  );
}

const router = createBrowserRouter({
  protectedRoutes: [
    {
      path: '*',
      element: (
        <UserContextProvider>
          <FeatureFlagsProvider>
            <AppInit>
              <Layout>
                <Suspense fallback={<SuspenseLoading />}>
                  <RoutingErrorBoundary>
                    <Outlet />
                  </RoutingErrorBoundary>
                </Suspense>
              </Layout>
            </AppInit>
          </FeatureFlagsProvider>
        </UserContextProvider>
      ),
      children: [
        { index: true, element: <LandingRedirect /> },

        // These routes are only available to ONCI users
        {
          element: <NotFoundIfNotONUser />,
          children: [
            { path: 'ingestion/*', element: <Ingestion /> },
            { path: 'internal/climate-model-validator/*', element: <ClimateModelValidator /> },
            { path: 'internal/custom-stress-scenarios/*', element: <CustomStressScenarios /> },
            { path: 'internal/scenario-validation/*', element: <ScenarioValidation /> },
            { path: 'internal/forecast-assumptions/*', element: <ForecastAssumptions /> },
            { path: 'internal/cre-forecast-assumptions/*', element: <CREForecastAssumptions /> },
            { path: 'internal/calc/*', element: <Calc /> },
            { path: 'internal/aggregation/*', element: <Aggregation /> },
            { path: 'internal/explainability/*', element: <Explainability /> },
          ],
        },
        {
          element: <LoanbookAnalysisSwitcher isLoanbookAnalysisFeature={false} />,
          children: [
            { path: 'climate/*', element: <Climate /> },
            { path: 'scenarios/*', element: <Scenarios /> },
            { path: 'monitoring/*', element: <Monitoring /> },
            { path: 'climate-scorecard/*', element: <ClimateScorecard /> },
            { path: 'file-repository/*', element: <FileRepository /> },
            { path: 'release-notes/*', element: <AppReleaseNotes /> },
            {
              path: 'diagnostics/*',
              children: [
                { path: '', element: <Diagnostics /> },
                { path: 'excluded-borrowers/:borrowerId', element: <ExcludedBorrowerOverview /> },
                { path: 'excluded-borrowers/*', element: <ExcludedBorrowersPage /> },
              ],
            },
            {
              path: 'support/*',
              element: <Support />,
            },
          ],
        },
        {
          element: <LoanbookAnalysisSwitcher isLoanbookAnalysisFeature={true} />,
          children: [{ path: 'loanbook-analysis/*', element: <LoanbookAnalysis /> }],
        },
        { path: '*', element: <PageNotFound /> },
      ],
    },
  ],
  publicRoutes: [
    {
      path: '*',
      element: (
        <Layout>
          <Suspense fallback={<SuspenseLoading />}>
            <RoutingErrorBoundary>
              <Outlet />
            </RoutingErrorBoundary>
          </Suspense>
        </Layout>
      ),
      children: [
        { path: 'scorecard-verify/:userId', element: <ClimateScorecardVerifyEmail /> },
        { path: '*', element: <PageNotFound /> },
      ],
    },
  ],
});

// Product name - we don't translate it (currently?)
// Keep this in sync with `index.html` so that the title doesn't change during loading.
const APP_TITLE = 'OakNorth Credit Intelligence Suite';
// Specific pages can add their own titles - they take the place of %s
// eslint-disable-next-line @oaknorth/ui/no-template-literals -- page title concatenation.
const APP_TITLE_TEMPLATE = `%s | ${APP_TITLE}`;

export const App = () => (
  <ThemeProvider>
    <HelmetProvider>
      <MediaContextProvider>
        <I18nProvider value={i18n}>
          <Helmet defaultTitle={APP_TITLE} titleTemplate={APP_TITLE_TEMPLATE} />
          <ErrorBoundary>
            <RestProvider>
              <Suspense fallback={<SuspenseLoading />}>
                <RouterProvider router={router} />
              </Suspense>
              {/* @ts-expect-error -- to check whether we're running in Cypress */}
              {!window.Cypress && <ReactQueryDevtools initialIsOpen={false} />}
            </RestProvider>
          </ErrorBoundary>
        </I18nProvider>
      </MediaContextProvider>
    </HelmetProvider>
  </ThemeProvider>
);
